import { useMemo, useState, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Award,
  CheckCircle2,
  Clock,
  Inbox,
  Paperclip,
  XCircle,
} from 'lucide-react';
import moment from 'moment';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  useBadgeApplicationsQuery,
  useBadgeStatsQuery,
} from '@/client/api/badges/badges.query';
import {
  APPLICATION_STATUS_BADGE,
  BadgeApplication,
  parseAsidJson,
} from '@/client/types/badges';
import { useListFilters } from '@/client/hooks/useListFilters';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import Pagination from '@/client/components/common/Pagination';
import BadgeApplicationFilters, {
  BadgeApplicationFiltersState,
} from '@/client/components/admin/Badge/BadgeApplicationFilters';
import ApplicationReviewDrawer from '@/client/components/admin/Badge/ApplicationReviewDrawer';

const PAGE_SIZE = 20;

const StatCard = ({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: string | number;
  icon: ReactNode;
  color: string;
}) => (
  <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
    <div className="flex items-center gap-4">
      <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${color}`}>
        {icon}
      </div>
      <div>
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <p className="mt-0.5 text-2xl font-black text-gray-900">{value}</p>
      </div>
    </div>
  </div>
);

const BadgeApplicationsPage = () => {
  useBreadcrumbs([{ label: 'Badges' }, { label: 'Review queue' }]);

  const { filters, page, handlePageChange, handleFilterChange, resetFilters } =
    useListFilters<BadgeApplicationFiltersState>({
      // `status` defaults to the review queue; useListFilters persists both this
      // and `btid` in the URL (?status=&btid=), so a reload restores the view.
      initialFilters: { status: 'under_review', btid: '' },
      initialLimit: PAGE_SIZE,
    });

  const queryParams = useMemo(
    () => ({
      status: filters.status || undefined,
      btid: filters.btid || undefined,
      page,
      page_size: PAGE_SIZE,
    }),
    [filters.status, filters.btid, page],
  );

  const { data, isLoading, isError } = useBadgeApplicationsQuery(queryParams);
  const { data: stats } = useBadgeStatsQuery();

  const [selected, setSelected] = useState<BadgeApplication | null>(null);

  const applications = data?.applications ?? [];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
              <Award className="h-6 w-6 text-amber-600" />
              Badge review queue
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Review applicant evidence and approve or reject credential applications.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to="/admin/badges/types">Badge types</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/admin/badges/award">Award / Revoke</Link>
            </Button>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Under review"
            value={stats?.under_review ?? '—'}
            icon={<Clock className="h-6 w-6 text-amber-600" />}
            color="bg-amber-50"
          />
          <StatCard
            label="Approved"
            value={stats?.approved ?? '—'}
            icon={<CheckCircle2 className="h-6 w-6 text-green-600" />}
            color="bg-green-50"
          />
          <StatCard
            label="Rejected"
            value={stats?.rejected ?? '—'}
            icon={<XCircle className="h-6 w-6 text-red-600" />}
            color="bg-red-50"
          />
          <StatCard
            label="Total applications"
            value={stats?.total_applications ?? data?.total ?? '—'}
            icon={<Inbox className="h-6 w-6 text-blue-600" />}
            color="bg-blue-50"
          />
        </div>

        <BadgeApplicationFilters
          filters={filters}
          onChange={handleFilterChange}
          onClear={resetFilters}
        />

        <div className="overflow-hidden rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Badge', 'Applicant', 'Submitted', 'Evidence', 'Status', ''].map(
                  (heading, index) => (
                    <th
                      key={heading || index}
                      className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${
                        heading === '' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {heading}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading &&
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index}>
                    {Array.from({ length: 6 }).map((__, cell) => (
                      <td key={cell} className="px-6 py-4">
                        <Skeleton className="h-4 w-24" />
                      </td>
                    ))}
                  </tr>
                ))}

              {!isLoading && isError && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm text-red-600">
                    Could not load badge applications. Try again.
                  </td>
                </tr>
              )}

              {!isLoading && !isError && applications.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500">
                    No applications match these filters.
                  </td>
                </tr>
              )}

              {applications.map((app) => {
                const asids = parseAsidJson(app.asid_json);
                const statusClass =
                  APPLICATION_STATUS_BADGE[app.status] ?? 'bg-gray-100 text-gray-700';
                return (
                  <tr
                    key={app.baid}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setSelected(app)}
                  >
                    <td className="px-6 py-4">
                      <span className="block text-sm font-medium text-gray-900">
                        {app.badge_name || `Type ${app.btid}`}
                      </span>
                      <span className="block text-xs text-gray-500">
                        baid {app.baid} · btid {app.btid}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                      {app.uid
                        ? `uid ${app.uid}`
                        : app.oid
                          ? `oid ${app.oid}`
                          : '—'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {app.date_submitted
                        ? moment(app.date_submitted).format('YYYY-MM-DD HH:mm')
                        : '—'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                      <span className="inline-flex items-center gap-1">
                        <Paperclip className="h-3.5 w-3.5 text-gray-400" />
                        {asids.length}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClass}`}
                      >
                        {String(app.status).replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelected(app);
                        }}
                      >
                        Review
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="border-t border-gray-200 px-4 py-3 sm:px-6">
            <Pagination
              total={data?.total ?? 0}
              page={page}
              limit={PAGE_SIZE}
              setPage={handlePageChange}
              showButtons
            />
          </div>
        </div>
      </div>

      <ApplicationReviewDrawer
        application={selected}
        isOpen={Boolean(selected)}
        onClose={() => setSelected(null)}
      />
    </div>
  );
};

export default BadgeApplicationsPage;
